import React, { useState } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import { controlHeight } from '../../lib/tiers';

// TAKING A REQUEST BACK — #590. ONE SHEET FOR BOTH KINDS OF REQUEST (gate 371
// D31-D37 for a transfer, gate 391 D13-D19 for a purchase request), taking the
// consequence and the audience as props: gate 391 F5 forbids a second sheet
// beside the first, because two sheets for one act is how the words drift.
//
// "Withdraw", never "Cancel" (371 D37): every sheet in Stede already uses
// Cancel to mean "close this without doing anything", and a destructive Cancel
// beside a harmless one is a mistake waiting for a thumb. The way out says
// "Keep it" / "Keep waiting".
//
// The reason is required (D34) and its label says who reads it, so it is a
// message to a person rather than a form field. The consequence is said
// before the press, in numbers (D35).
//
// Colors measured, not assumed (371 F4): the app's .btn-danger is red on pale
// red and measures 3.3; the committing button here is white on #A32D2D (7.07).
const DANGER = '#A32D2D';

export default function WithdrawSheet({
  visible, onClose, onDone,
  phoneLayout, coarse,
  title,            // "Withdraw this request?" / "Stop the rest of this request?"
  subtitle,         // the request's label
  consequence,      // what happens, in numbers
  reasonLabel,      // "Why? Houston will see this."
  blockedReason,    // "Say why, so Houston knows."
  placeholder = 'The job was canceled, or the wrong item was asked for',
  keepLabel = 'Keep it',
  submitLabel = 'Withdraw request',
  busyLabel = 'Withdrawing…',
  onSubmit,         // async (reason) => { ok } | { error }
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!visible) return null;

  const blocked = reason.trim() ? null : blockedReason;

  async function submit() {
    setBusy(true);
    setErr(null);
    const r = await onSubmit(reason.trim());
    setBusy(false);
    // F1 Error: inline, beside the button, in the database's own sentence --
    // and the typed reason survives, because nothing here clears it.
    if (r?.error) { setErr(r.error.message || 'That did not save. Nothing was withdrawn — try again.'); return; }
    onDone?.(r);
  }

  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');

  return (
    <Overlay variant={phoneLayout ? 'sheet' : 'modal'} onClose={busy ? undefined : onClose} maxWidth={460}>
      <div style={{ padding: '1.1rem 1.15rem 1.4rem' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0F0F0F' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: '#3A3A3A', marginTop: 3 }}>{subtitle}</div>}
        <div style={{ fontSize: 14, color: '#1A1A1A', marginTop: 12, lineHeight: 1.5 }}>{consequence}</div>

        <label htmlFor="withdraw-reason" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#3A3A3A', marginTop: 14, marginBottom: 5 }}>
          {reasonLabel}
        </label>
        <textarea
          id="withdraw-reason"
          rows={3}
          value={reason}
          onChange={e => { setReason(e.target.value); setErr(null); }}
          placeholder={placeholder}
          // 16px minimum -- locked rule 12, iOS zooms the page otherwise.
          // The face and the 16px floor both come from index.css's baseline
          // (#166, #601), so this sets neither.
          style={{ width: '100%', padding: '9px 10px', border: '1px solid #CFCFCF', borderRadius: 8, boxSizing: 'border-box' }}
        />

        {err && (
          <div role="alert" style={{
            marginTop: 10, padding: '9px 11px', borderRadius: 8,
            background: '#FDF1F1', border: '1px solid #F0CFCF', fontSize: 13, color: '#8E2F2F', fontWeight: 600,
          }}>{err}</div>
        )}

        <div style={{
          display: 'flex', flexDirection: phoneLayout ? 'column' : 'row-reverse',
          justifyContent: phoneLayout ? undefined : 'flex-start',
          alignItems: phoneLayout ? 'stretch' : 'center', gap: 10, marginTop: 16,
        }}>
          {/* THE BLOCKED REASON SITS UNDER ITS OWN BUTTON. ActionButton renders
              the button and its reason as siblings, so in a row the reason
              became a third item BETWEEN the two buttons -- seen on a desk. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: phoneLayout ? 'stretch' : 'flex-end' }}>
          <ActionButton
            onClick={submit}
            blockedReason={blocked}
            reasonStyle={{ fontSize: 12, color: '#48545F', marginTop: 6 }}
            busy={busy}
            busyLabel={busyLabel}
            className="btn"
            style={{
              background: DANGER, color: '#FFFFFF', border: `1px solid ${DANGER}`,
              justifyContent: 'center', minHeight: commitH,
              ...(phoneLayout ? { width: '100%' } : { minWidth: 170 }),
            }}
          >
            {submitLabel}
          </ActionButton>
          </div>
          <button onClick={onClose} disabled={busy} className="btn btn-secondary"
            style={{ justifyContent: 'center', minHeight: minH, ...(phoneLayout ? { width: '100%' } : { minWidth: 120 }) }}>
            {keepLabel}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
